import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Badge, Card, EmptyState, Input, Table } from '@/admin-design/_common'
import type { BadgeVariant } from '@/admin-design/_common'
import { api } from '../api/client'

export default function Income() {
  const [days, setDays] = useState(7)
  const { data, isLoading } = useQuery({
    queryKey: ['income', days],
    queryFn: () => api.income(days),
    refetchInterval: 60000,
  })
  const items: any[] = Array.isArray(data) ? data : []

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">주문 관리</h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">조회 기간</span>
          <Input
            type="number"
            className="w-16"
            value={days}
            min={1}
            max={30}
            onChange={(e) => setDays(Number(e.target.value) || 7)}
          />
          <span className="text-sm text-muted-foreground">일</span>
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">주문 조회 중...</p>}

      <Card className="overflow-hidden overflow-x-auto">
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header>주문일</Table.Header>
              <Table.Header>상품</Table.Header>
              <Table.Header className="text-right">수량</Table.Header>
              <Table.Header className="text-right">결제금액</Table.Header>
              <Table.Header>상태</Table.Header>
              <Table.Header>수령자</Table.Header>
              <Table.Header>배송지</Table.Header>
              <Table.Header>도매처</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {items.map((o: any) => (
              <Table.Row key={o.product_order_id}>
                <Table.Cell className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDate(o.order_date)}
                </Table.Cell>
                <Table.Cell>
                  <div className="text-sm max-w-xs truncate">{o.product_name}</div>
                  {o.product_option && (
                    <div className="text-xs text-muted-foreground">{o.product_option}</div>
                  )}
                </Table.Cell>
                <Table.Cell className="text-right">{o.quantity}</Table.Cell>
                <Table.Cell className="text-right tabular-nums whitespace-nowrap">
                  {o.total_payment?.toLocaleString()}원
                </Table.Cell>
                <Table.Cell>
                  <OrderStatusBadge status={o.status} />
                </Table.Cell>
                <Table.Cell>
                  <div className="text-sm">{o.receiver_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{o.receiver_tel}</div>
                </Table.Cell>
                <Table.Cell>
                  <div className="text-xs max-w-[200px]">
                    <span className="text-muted-foreground">[{o.receiver_zip}]</span>{' '}
                    {o.receiver_address}
                  </div>
                  {o.shipping_memo && (
                    <div className="text-xs text-muted-foreground italic mt-0.5">
                      {o.shipping_memo}
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  {o.provider_url ? (
                    <a
                      href={o.provider_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline"
                    >
                      {o.provider} &rarr;
                    </a>
                  ) : (
                    <span className="text-xs text-muted-foreground">{o.seller_code || '-'}</span>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
            {items.length === 0 && !isLoading && (
              <Table.Row className="hover:bg-transparent">
                <Table.Cell colSpan={8} className="p-0">
                  <EmptyState title="주문이 없습니다" />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Card>
    </div>
  )
}

function formatDate(s: string) {
  if (!s) return '-'
  try {
    const d = new Date(s)
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  } catch {
    return s
  }
}

function OrderStatusBadge({ status }: { status: string }) {
  const map: Record<string, [BadgeVariant, string]> = {
    PAYMENT_WAITING: ['warning', '입금대기'],
    PAYED: ['info', '결제완료'],
    DELIVERING: ['info', '배송중'],
    DELIVERED: ['success', '배송완료'],
    PURCHASE_DECIDED: ['success', '구매확정'],
    CANCELED: ['destructive', '취소'],
    RETURNED: ['destructive', '반품'],
    EXCHANGED: ['warning', '교환'],
  }
  const [variant, label] = map[status] ?? ['muted', status]
  return <Badge variant={variant}>{label}</Badge>
}
